import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { Icon } from '../../Icon';
import { scrollInputToCenter } from '../../../utils/helpers/common';
import {
  StyledField,
  StyledTextField,
  StyledTextLabel,
  TextFieldContainer,
  ValueVisibilityToggle
} from './styled';
import { useToggleInputValueVisible } from './hooks/useToggleInputValueVisible';
import { VISIBLE_VALUE_ICON_SIZE } from './constants';
import EyeIcon from '../../../public/static/images/eye.svg';
import CloseEyeIcon from '../../../public/static/images/closed-eye.svg';
import { useTextFieldChange } from '../../../hooks/useTextFieldChange';
import { useHideCaretUnderHeader } from './hooks/useHideCaretUnderHeader';
import { TextFieldProps } from './types';

export const TextField: React.FC<TextFieldProps> = ({
  stat,
  name,
  cypress,
  icon,
  value,
  format,
  onClick,
  disabled = false,
  inputMode = 'text',
  onChange,
  maxLength,
  placeholder,
  isValueHidden,
  isFloat = false,
  styleType = 'default',
  type = 'text',
  formValues = {},
  isEnabledAutoSelectionRange = true,
  headerRef,
  containerRef
}) => {
  const isNum = inputMode === 'numeric';
  const numRegex = isFloat ? '[0-9.,]*' : '[0-9]*';
  const inputRef = useRef<HTMLInputElement | null>(null);
  const onChangeTextField = useTextFieldChange(formValues);

  const [isFocused, setIsFocused] = useState(false);
  const [canMaskWithCss, setCanMaskWithCss] = useState(false);

  useEffect(() => {
    if (!isValueHidden) return;

    setCanMaskWithCss(
      typeof CSS !== 'undefined' &&
        typeof CSS.supports === 'function' &&
        CSS.supports('-webkit-text-security', 'disc')
    );
  }, [isValueHidden]);

  const { isValueVisible, toggleValueVisibility } = useToggleInputValueVisible(
    inputRef,
    { restoreFocusOnToggle: !canMaskWithCss }
  );

  const isMasked = !!isValueHidden && !isValueVisible;

  const maskedInputProps = isValueHidden
    ? { autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }
    : {};

  const { resetInputCaretStyle } = useHideCaretUnderHeader(
    headerRef,
    inputRef,
    isFocused,
    containerRef
  );

  const handleFocus = () => {
    setIsFocused(true);
    inputRef.current?.focus();
    resetInputCaretStyle();
  };

  const handleBlur = (e: FocusEvent) => {
    const currentValue = (e.target as HTMLInputElement).value;
    if (!currentValue) setIsFocused(false);
  };

  useEffect(() => {
    const inputElement = inputRef.current;
    if (inputElement) {
      inputElement.addEventListener('focus', handleFocus);
      inputElement.addEventListener('blur', handleBlur);

      return () => {
        inputElement.removeEventListener('focus', handleFocus);
        inputElement.removeEventListener('blur', handleBlur);
      };
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) {
      e.preventDefault();
      return;
    }

    if (typeof onChange === 'function') {
      onChange(e, name, isNum, isFloat);
      return;
    }
    onChangeTextField(e, name, isNum, isFloat);
  };

  const parseValue = (val: string) =>
    isNum && isFloat ? val.replace(',', '.') : val;

  const parseTextFieldValue = (val: unknown) =>
    inputMode === 'numeric' ? parseValue(val as string) : val;

  const fieldFormat = format as
    | ((value: unknown, name: string) => unknown)
    | undefined;

  const handleClick = () => {
    if (onClick) onClick();
    handleFocus();
  };

  const scrollHandler = () => scrollInputToCenter(inputRef.current);
  const preventFocusSteal = (e: React.SyntheticEvent) => e.preventDefault();

  useLayoutEffect(() => {
    if (isEnabledAutoSelectionRange) {
      requestAnimationFrame(() => {
        const input = inputRef.current;

        if (
          input &&
          input.ownerDocument &&
          input.ownerDocument.contains(input)
        ) {
          try {
            const { length } = input.value || '';
            input.setSelectionRange(length, length);
          } catch {
            // Silently ignore Range API errors if element was removed
          }
        }
      });
    }
  }, [type, isEnabledAutoSelectionRange]);

  const dataCy = cypress || 'text-field';

  return (
    <TextFieldContainer data-cy={`${dataCy}-container`}>
      <StyledTextField
        data-stat={stat}
        data-cy="text-field"
        onClick={handleClick}
        onFocus={handleFocus}
      >
        <StyledTextLabel
          role="presentation"
          data-cy={`${dataCy}-label`}
          $isBold={styleType === 'bold'}
          $isActive={isFocused || !!value}
        >
          {placeholder}
        </StyledTextLabel>
        <StyledField
          name={name}
          value={value}
          ref={inputRef}
          format={fieldFormat}
          formatOnBlur={isFloat}
          component="input"
          autoComplete="off"
          disabled={disabled}
          maxLength={maxLength}
          onFocus={scrollHandler}
          onChange={handleChange}
          data-cy={cypress || 'text-field-input'}
          $isBold={styleType === 'bold'}
          pattern={isNum ? numRegex : null}
          parse={parseTextFieldValue}
          inputMode={isFloat ? 'decimal' : inputMode}
          type={isMasked && !canMaskWithCss ? 'password' : type}
          $isMasked={isMasked && canMaskWithCss}
          {...maskedInputProps}
        />
      </StyledTextField>
      {icon && (
        <Icon IconComponent={icon} width={35} height={35} indent={false} />
      )}
      {isValueHidden && value && (
        <ValueVisibilityToggle
          type="button"
          aria-label={isValueVisible ? 'Hide password' : 'Show password'}
          onMouseDown={preventFocusSteal}
          onPointerDown={preventFocusSteal}
          onClick={toggleValueVisibility}
        >
          <Icon
            color="transparent"
            indent={false}
            width={VISIBLE_VALUE_ICON_SIZE}
            height={18}
            IconComponent={isValueVisible ? EyeIcon : CloseEyeIcon}
          />
        </ValueVisibilityToggle>
      )}
    </TextFieldContainer>
  );
};
